import { Button, Stack, Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import Logo from '@/components/Icon/Logo';
import { Screen } from '@/components/Layout/Screen';

import { ROUTE } from '@/constants/routes';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useApplicationStatuses } from '@/hooks/useApplicationStatuses';
import { useTranslate } from '@/hooks/useTranslate';

import { signOut } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';

import {
  AdultContactForm,
  AdultContactFormType,
} from '../components/ApplicationTypeSelection/AdultContactForm';

const AddAdultContactScreen: React.FC = () => {
  const { logo } = useAppSelector(selectConfigsState);
  const { t, language } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectLoggedInUser);
  const [adultContact, setAdultContact] = useState<AdultContactFormType>();
  const { isJointAdultAgeRestriction } = useApplicationStatuses();

  const handleBackClick = () => {
    if (isJointAdultAgeRestriction) {
      dispatch(signOut());
    } else {
      navigate(ROUTE.ACCOUNT_TYPE);
    }
  };

  const handleSubmitSuccess = (data: AdultContactFormType) => {
    setAdultContact(data);
  };

  const adultEmail = adultContact?.email || user?.adultEmail;

  return (
    <Screen
      header={{
        centerSlot: (
          <ProgressDots
            value={PROGRESS_STEPS.thirdPartyComplianceCheckStep.value}
            total={PROGRESS_STEPS.thirdPartyComplianceCheckStep.total}
          />
        ),
      }}
      bgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <Logo src={logo} alt="Logo" />
      <Stack direction="column" gap={3} flex={1}>
        {adultEmail ? (
          <>
            <Typography variant="h3" textAlign="center" mb={1}>
              {t('page.addAdultContact.result.title')}
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              mb={2}
              maxWidth={320}
            >
              <Trans
                i18nKey="page.addAdultContact.result.content"
                language={language}
                as="div"
                values={{
                  adultEmail,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h3" textAlign="center" mb={1}>
              {t('page.addAdultContact.title')}
            </Typography>
            <AdultContactForm onSubmitted={handleSubmitSuccess} />
          </>
        )}
      </Stack>
      <Button
        onClick={handleBackClick}
        variant="text"
        {...getDataTestId(
          isJointAdultAgeRestriction
            ? 'back-to-login'
            : 'go-back-to-application',
        )}
      >
        {isJointAdultAgeRestriction
          ? t('page.addAdultContact.button.backToLogin')
          : t('common.button.backToApplication')}
      </Button>
    </Screen>
  );
};

export default AddAdultContactScreen;
